import {inject, Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';

import {API_TASK, API_TASK_STEP, API_TASKS} from '../../config/urls';
import {UrlService} from '@shared/services/url.service';
import {TaskListDto} from '@dto/tasks/task-list.dto';
import {TaskDetailDto} from '@dto/tasks/task-detail.dto';
import {TaskStepDetailDto} from '@dto/tasks/task-step-detail.dto';
import {TaskDto} from '@dto/tasks/task.dto';

@Injectable({providedIn: 'root'})
export class TaskService {

  private readonly http = inject(HttpClient);
  private readonly urlService = inject(UrlService);
  private readonly _taskSummaryCache = new Map<string, { data: TaskDto; cachedAt: number }>();
  private readonly CACHE_TTL_MS = 60_000;

  public getTasks(): Observable<TaskListDto> {
    return this.http.get<TaskListDto>(API_TASKS, { observe: 'response' }).pipe(
      map(response => this.normalizeTaskList(response.body)),
      tap(taskList => {
        // Repopulate from scratch so the cache never grows beyond the current task list.
        this._taskSummaryCache.clear();
        const now = Date.now();
        taskList.tasks.forEach(task => this._taskSummaryCache.set(task.id, { data: task, cachedAt: now }));
      })
    );
  }

  public getTaskSummary(taskId: string): Observable<TaskDto> {
    const entry = this._taskSummaryCache.get(taskId);
    if (entry && Date.now() - entry.cachedAt < this.CACHE_TTL_MS) {
      return of(entry.data);
    }
    // Cache miss or TTL expired: fetch full list and filter (direct navigation / page refresh).
    return this.getTasks().pipe(
      map(taskList => {
        const found = taskList.tasks.find(t => t.id === taskId);
        if (!found) {
          throw new Error(`Task ${taskId} not found`);
        }
        return found;
      })
    );
  }

  /** Call after a step submission or any mutation to force the next summary lookup to re-fetch. */
  public invalidateTaskSummary(taskId: string): void {
    this._taskSummaryCache.delete(taskId);
  }

  public getTask(taskId: string): Observable<TaskDetailDto> {
    const endpointUrl = this.urlService.buildUrl(API_TASK, taskId);
    return this.http.get<TaskDetailDto>(endpointUrl, { observe: 'response' }).pipe(
      map(response => this.normalizeTaskDetail(response.body))
    );
  }

  public getTaskStep(taskId: string, taskStepId: number): Observable<TaskStepDetailDto> {
    const endpointUrl = this.urlService.buildUrl(API_TASK_STEP, taskId, String(taskStepId));
    return this.http.get<TaskStepDetailDto>(endpointUrl);
  }

  private normalizeTaskList(taskListDto: TaskListDto | null): TaskListDto {
    const dto = taskListDto ?? { tasks: [] };
    return {
      ...dto,
      tasks: dto.tasks ?? []
    } as TaskListDto;
  }

  private normalizeTaskDetail(taskDetailDto: TaskDetailDto | null): TaskDetailDto {
    if (taskDetailDto === null) {
      throw new Error('Task detail response is empty');
    }

    return {
      ...taskDetailDto,
      steps: taskDetailDto.steps ?? []
    };
  }
}
